import { useEffect } from "react";
import { Controller } from "react-hook-form";
import { Nullish } from "utility-types";
import { ValidationError } from "joi";

import { useSelectTheme, useSelectWorkspaces } from "../../store";
import { v, RIF, useForm, useCurrentWorkspaceState } from "../../lib";
import { InstructionIcon } from "../../asset/image";
import { Textarea, LabelBox } from "..";

const INSTRUCTION_WORD_COUNT_MAXIMUM = 250;

export interface InstructionBlockPropsT {
  instructionValue: string | Nullish;
  onChange: (value: string | undefined) => void;
  onValidated: () => void;
  onError: (error: ValidationError) => void;
  isSummaryReview?: boolean;
}

interface FormData {
  participantInstructions?: string;
}

const schema = v.object<FormData>({
  participantInstructions: v.string().min(1).max(250).required(),
});

export const InstructionBlock = (props: InstructionBlockPropsT) => {
  const { pad, color, fontWeight, fontSize } = useSelectTheme();
  const workspacesState = useSelectWorkspaces();
  const { workspaceId } = useCurrentWorkspaceState();
  const role = workspacesState.roleInWorkspaces[workspaceId ?? ""] || "owner";

  const { instructionValue, onChange, onValidated, onError, isSummaryReview } = props;

  const { control, watch, reset, getValues } = useForm<FormData>({
    schema,
    shouldFocusError: false,
  });

  useEffect(() => {
    if (instructionValue) {
      const { participantInstructions } = getValues();
      if (instructionValue !== participantInstructions) {
        reset({ participantInstructions: instructionValue });
      }
    }
  }, [instructionValue, reset, getValues]);

  const participantInstructionsField = watch("participantInstructions");

  useEffect(() => {
    const form: FormData = {
      participantInstructions: participantInstructionsField,
    };
    onChange(form.participantInstructions);

    const result = schema.validate(form);
    if (!result.error) {
      onValidated();
    } else {
      onError(result.error);
    }
  }, [participantInstructionsField]);

  return (
    <div
      css={{
        display: "flex",
        background: color.white,
        borderRadius: "5px;",
        width: "55vw",
        boxShadow: "0px 4px 12px 0px #D4D4D440",
        marginBottom: pad.large,
      }}
    >
      {/* left */}
      <div
        css={{
          padding: pad.large,
          borderRight: `1px solid ${color.grey_100}`,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          width: "26%",
        }}
      >
        <div>
          <div
            css={{
              width: "40px",
              height: "40px",
              background: color.background,
              borderRadius: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: pad.large,
            }}
          >
            <img src={InstructionIcon} width="24px" alt="" aria-hidden />
          </div>
          <p
            css={{
              fontSize: "18px",
              fontWeight: fontWeight.thick,
              marginBottom: pad.medium,
            }}
          >
            Instructions
          </p>
          {RIF(
            !isSummaryReview,
            <p css={{ lineHeight: "19px", fontWeight: fontWeight.regular }}>
              Give instructions for onboarding participants on how to get started and important
              things to remember.
            </p>,
          )}
        </div>

        {/* {RIF(!isSummaryReview,
          <div css={{
            width: '100%',
            height: '149px',
            background: color.background,
            borderRadius: '5px',
          }}></div>)} */}
      </div>

      {/* right */}
      <div css={{ padding: pad.xl, width: "74%" }}>
        {RIF(
          !isSummaryReview,
          <p css={{ fontSize: "14px", fontWeight: fontWeight.medium, marginBottom: pad.xl }}>
            Information for onboarding participants:
          </p>,
        )}

        {RIF(
          !isSummaryReview,
          <Controller
            control={control}
            name="participantInstructions"
            render={({ field: { name, value, onChange } }) => (
              <LabelBox
                {...{
                  label: "Instructions",
                  required: true,
                  leftCharacters:
                    INSTRUCTION_WORD_COUNT_MAXIMUM - (participantInstructionsField?.length ?? 0),
                }}
              >
                <Textarea
                  {...{
                    name,
                    value: value ?? "",
                    onChange,
                  }}
                  maxLength={INSTRUCTION_WORD_COUNT_MAXIMUM}
                  rows={4}
                  readOnly={role === "viewer"}
                  placeholder="Please write instructions for onboarding participants"
                />
              </LabelBox>
            )}
          />,
        )}

        {RIF(
          isSummaryReview && instructionValue,
          <div css={{ margin: pad.medium }}>{instructionValue}</div>,
        )}

        {RIF(
          !isSummaryReview,
          <>
            <p
              css={{
                fontSize: fontSize.h7,
                color: color.textIcon.secondary,
                marginBottom: pad.widthPadding,
                marginTop: pad.xl,
              }}
            >
              Example 1: Please read all of the provided documents thoroughly and wear your device
              at all times.
            </p>
            <p css={{ fontSize: fontSize.h7, color: color.textIcon.secondary }}>
              Example 2: Please remember to charge your device and sync it regularly by opening the
              app.
            </p>
          </>,
        )}
      </div>
    </div>
  );
};
